import React from "react";
import { useGetTncCurrentMsaQuery } from "../store/slices/settingsSlice";
import BillingDetailsIcon from "../../assets/icons/invoice_history.svg2";
import MyIcon from "../Common/MyIcon";

const { countries, defaultCountry } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;

function formatLabel(value) {
  if (!value) return value;
  return value
    .replace(/_/g, " ")
    .replace(/^./, (character) => character.toUpperCase());
}

function formatMoney(value, currency) {
  const amount = Number(value);
  const formattedAmount = Number.isFinite(amount) ? amount.toFixed(2) : value;
  return currency ? `${currency} ${formattedAmount}` : formattedAmount;
}

function getCountryName(countryCode) {
  if (countryCode === "--") return defaultCountry?.country_name;
  return countries.find((country) => country.country_code === countryCode)?.country_name || countryCode;
}

function formatDisplayValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (React.isValidElement(value)) return value;
  if (Array.isArray(value)) {
    const values = value.map(formatDisplayValue).filter(Boolean);
    return values.length ? values.join(", ") : null;
  }
  if (typeof value === "object") {
    const values = Object.values(value).map(formatDisplayValue).filter(Boolean);
    return values.length ? values.join(", ") : null;
  }
  return value;
}

function Row({ label, value }) {
  const displayValue = formatDisplayValue(value);
  if (displayValue === null || displayValue === undefined || displayValue === "") return null;
  return (
    <tr>
      <td className="fw-semibold text-nowrap pe-3 py-1 text-secondary">{label}</td>
      <td className="py-1" style={{ wordBreak: "break-word" }}>
        {displayValue}
      </td>
    </tr>
  );
}

function SectionHeader({ children }) {
  return (
    <tr>
      <td colSpan={2}>
        <hr className="my-2 text-secondary" />
        <span className="fw-bold small text-secondary">{children}</span>
      </td>
    </tr>
  );
}

function commaSeparated(value) {
  return formatDisplayValue(value);
}

function objectValues(value) {
  if (!value || typeof value !== "object") return [];
  return Object.values(value);
}

function formatServiceCharges(serviceCharges) {
  return objectValues(serviceCharges)
    .map((charge) => `${charge.product}: ${charge.cost}`)
    .join("\n");
}

function formatCountryRates(countryRates, currency) {
  if (Array.isArray(countryRates?.rates)) {
    return countryRates.rates
      .map((rate) => {
        const countryCode = rate.country_iso || rate.iso_code || rate.country_name;
        const country = getCountryName(countryCode);
        const defaultCountrySuffix = countryCode === "--" ? " (Default country)" : "";
        const product = formatLabel(rate.for || rate.product);
        const charge = formatMoney(rate.charge, currency);

        return country ? `${product} - ${country}${defaultCountrySuffix}: ${charge}` : `${product}: ${charge}`;
      })
      .join("\n");
  }

  return objectValues(countryRates)
    .map((rate) => {
      const country = rate.country_name || rate.iso_code;
      const rates = Object.entries(rate)
        .filter(([key]) => !["country_name", "iso_code"].includes(key))
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");

      return rates ? `${country}: ${rates}` : country;
    })
    .join("\n");
}

function PreformattedRow({ label, value }) {
  if (!value) return null;
  return (
    <tr>
      <td className="fw-semibold text-nowrap pe-3 py-1 text-secondary align-top">
        {label}
      </td>
      <td className="py-1">
        <pre
          className="mb-0 small"
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {value}
        </pre>
      </td>
    </tr>
  );
}

export default function AccountTNC() {
  const { data, isLoading, isError } = useGetTncCurrentMsaQuery();

  return (
    <div className="bg-purple-gradient-darker-reverse rounded-4 p-3">
      <h5 className="d-flex align-items-center mb-3">
        <MyIcon Icon={BillingDetailsIcon} className="me-2" />
        <span className="ms-2">
          <span className="fw-bold me-2">Terms &amp; Conditions (MSA)</span>
          <div className="small text-secondary">
            Current master service agreement details
          </div>
        </span>
      </h5>
      <hr className="my-3 text-secondary" />

      {isLoading && <p className="text-secondary small">Loading…</p>}
      {isError && <p className="text-danger small">Failed to load MSA data.</p>}

      {data && (
        <>
          {data.link && (
            <div className="mb-3">
              <a
                href={data.link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-colored-reverse rounded-pill fw-semibold"
              >
                View MSA document
              </a>
            </div>
          )}

          {data.data && (
            <div className="table-responsive">
              <table className="table table-sm table-borderless mb-0 small">
                <tbody>
                  <Row label="Service name"        value={commaSeparated(data.data.serviceName)} />

                  {Array.isArray(data.data.subscriptionsData) &&
                    data.data.subscriptionsData.map((subscription) => (
                      <React.Fragment key={`${subscription.clientProduct}-${subscription.clientName}`}>
                        <SectionHeader>
                          {subscription.clientProduct} - {subscription.clientName}
                        </SectionHeader>
                        <Row label="Service status"      value={subscription.serviceStatus} />
                        <Row label="Support package"     value={subscription.supportPackage} />
                        <Row label="Subscription start"  value={subscription.subscriptionStartDate} />
                        <Row label="Subscription end"    value={subscription.subscriptionEndDate} />
                        <Row label="Billing currency"    value={subscription.billingCurrency} />
                        <Row label="Authorised users"    value={subscription.authorisedUsers} />
                        <Row label="Communication allowance" value={subscription.inclusiveCommunicationAllowance} />
                        <Row label="Contract notice period"  value={subscription.contractNoticePeriod} />
                        <Row label="Payment notice period"   value={subscription.paymentNoticePeriod} />
                        <Row label="Service restrictions"    value={subscription.serviceRestrictions} />
                        <Row label="Additional features"     value={commaSeparated(subscription.additionalFeatures)} />
                        <PreformattedRow label="Service charges" value={formatServiceCharges(subscription.serviceCharges)} />
                        <PreformattedRow label="Country rates" value={formatCountryRates(subscription.countryRates, subscription.billingCurrency)} />
                      </React.Fragment>
                    ))}

                  {data.data.customer && (
                    <>
                      <SectionHeader>Customer</SectionHeader>
                      <Row label="Name"     value={data.data.customer.name} />
                      <Row label="Status"   value={data.data.customer.status} />
                      <Row label="MSA version" value={data.data.customer.msa_version} />
                      <Row label="Reference"   value={data.data.customer.reference} />
                    </>
                  )}

                  {Array.isArray(data.data.customer?.clients) &&
                    data.data.customer.clients.length > 0 && (
                      <>
                        <SectionHeader>Products</SectionHeader>
                        {data.data.customer.clients.map((client) => (
                          <tr key={client.id}>
                            <td className="fw-semibold text-nowrap pe-3 py-1 text-secondary">
                              {client.product_type}
                            </td>
                            <td className="py-1">
                              {client.client_name}
                              <span className="ms-2 badge bg-secondary small">
                                {client.active === "Y" ? "Active" : "Inactive"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
